// @owners { team: patients-team }
import { SPACING } from '@alto/design-library-tokens';
import {
  Body,
  Button,
  Card,
  Column,
  H1,
  Illustration,
  LgPadding,
  NavBarLogo,
  SmSpacing,
  XsSpacing,
  XxsSpacing,
} from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React, { useCallback, useEffect } from 'react';
import progynyLogo from '~shared/assets/images/progyny_by_alto_logo.png';
import rx_paper_questionmark from '~shared/assets/images/rx_paper_with_question_mark.png';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { getProgynyWizardFirstFill } from '~shared/selectors/checkout/progyny';
import { useSelectorShared } from '~shared/store';
import { ProgynyPage } from '../../shared/components/ProgynyPage';
import { getProgynyWizardNextRoute } from '../helpers/getProgynyWizardNextRoute';

export const ProgynyWizardIntro2 = () => {
  const { getCurrentRouteName, goBack, navigate } = useNavigation();
  const { trackPageView } = useAnalytics();
  const isRefill = !useSelectorShared(getProgynyWizardFirstFill);

  const onNext = useCallback(() => {
    const currentRoute = getCurrentRouteName();
    const nextRouteName = getProgynyWizardNextRoute(currentRoute);
    if (!nextRouteName) return;
    navigate(nextRouteName);
  }, [getCurrentRouteName, navigate]);

  useEffect(() => {
    trackPageView({
      event: EVENTS.PROGYNY_INTRO_2_VIEWED,
      params: {
        is_refill: isRefill,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ProgynyPage
      dismissIcon="chevronleft"
      onDismiss={goBack}
      NavBarCenterContent={<NavBarLogo source={progynyLogo} />}
      HeaderContent={
        <LgPadding>
          <Column center>
            <Illustration
              size="lg"
              source={rx_paper_questionmark}
            />
            <XsSpacing />
            <H1
              testID="e2e-progyny-step2-header"
              center
            >
              Why we calculate specific quantities for each order
            </H1>
          </Column>
        </LgPadding>
      }
      buttons={[
        <Button
          key="Continue"
          onPress={onNext}
          testID="e2e-progyny-step2-button"
        >
          Next
        </Button>,
      ]}
    >
      <Card>
        <LgPadding topPadding={SPACING.STATIC.MD}>
          <Body>
            Your Progyny benefits work by making sure you’re only getting and paying for the medication you need.
          </Body>
          <SmSpacing />
          <Body fontFamily="semibold">Your coverage allows:</Body>
          <Body>
            {'\u2022 '}First delivery: <Body fontFamily="semibold">up to 7 days</Body> of Gonal-f and Menopur, and{' '}
            <Body fontFamily="semibold">4 days</Body> of Cetrotide
          </Body>
          <XxsSpacing />
          <Body>
            {'\u2022 '}Each refill: <Body fontFamily="semibold">up to 3 days</Body> of Gonal-f and Menopur, and{' '}
            <Body fontFamily="semibold">2 days</Body> of Cetrotide
          </Body>
          <XxsSpacing />
          <Body>
            {'\u2022 '}Per authorization: <Body fontFamily="semibold">up to 7,650 total international units (IUs)</Body>{' '}
            of Gonal-f and Menopur. Track your remaining IU balance in your delivery confirmation email.
          </Body>

          <SmSpacing />
          <Body>You'll likely need around 3 deliveries to complete this cycle.</Body>

          <SmSpacing />
          <Body>
            If your treatment plan requires more than what is covered, you have the option to obtain additional
            medication by paying out of pocket.
          </Body>
        </LgPadding>
      </Card>
    </ProgynyPage>
  );
};
